import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/assessment", label: "Self-Assessment" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full border-b bg-[#F7F5F1]/85 backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "border-transparent shadow-[0_2px_20px_rgba(38,40,46,0.08)]" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-[#2F5D8C]" : "text-[#5B5E66] hover:text-[#26282E]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="rounded-md bg-[#2F5D8C] px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1] transition-colors duration-300 hover:bg-[#254A6E]"
          >
            Request a Consultation
          </Link>
        </nav>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#26282E] p-2"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#26282E]/10 bg-[#F7F5F1]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="py-3 font-serif text-2xl text-[#26282E] hover:text-[#2F5D8C] transition-colors"
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-button"
                className="mt-4 rounded-md bg-[#2F5D8C] px-5 py-3 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F5F1]"
              >
                Request a Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
